import {
  Carousel,
  EIconButtonSize,
  EIconButtonVariant,
  EPaginationVariant,
  GeneralCloseIcon,
  IconButton,
  Pagination,
} from '@outdoorsyco/bonfire';
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Swiper } from 'swiper';

import SlideN1Image from '@/assets/trips/claims-guide/1.jpeg';
import SlideN2Image from '@/assets/trips/claims-guide/2.jpeg';
import SlideN3Image from '@/assets/trips/claims-guide/3.jpeg';
import SlideN4Image from '@/assets/trips/claims-guide/4.jpeg';
import SlideN5Image from '@/assets/trips/claims-guide/5.jpeg';
import { trackClaimsCarouselViewedEvent } from '@/services/analytics/trips';

import { ClaimsGuideCarouselProgress } from './ClaimsGuideCarouselProgress';
import { ClaimsGuideCarouselSlide } from './ClaimsGuideCarouselSlide';
import { ClaimsGuideCarouselStartSlide } from './ClaimsGuideCarouselStartSlide';
import { useClaimsGuideModal } from './useClaimsGuideModal';

export const ClaimsGuideCarousel = () => {
  const intl = useIntl();

  const { onModalClose } = useClaimsGuideModal();

  const [slideNumber, setSlideNumber] = useState(1);
  const [swiperInstance, setSwiperInstance] = useState<Swiper>();

  const handleSlideChange = (targetSlideNumber: number) => {
    swiperInstance?.slideTo(targetSlideNumber - 1);
  };

  const handleStart = () => {
    swiperInstance?.slideTo(1);
  };

  const trackedViewedEventRef = useRef(false);
  const slideNumberRef = useRef(slideNumber);
  slideNumberRef.current = slideNumber;

  useEffect(() => {
    return () => {
      if (!trackedViewedEventRef.current) {
        trackClaimsCarouselViewedEvent({ slideNumber: slideNumberRef.current });
        trackedViewedEventRef.current = true;
      }
    };
  }, []);

  return (
    <div>
      <div className="absolute z-10 flex justify-end right-4 top-4 left-4">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <ClaimsGuideCarouselProgress swiperInstance={swiperInstance} />
        </div>

        <IconButton
          icon={GeneralCloseIcon}
          size={EIconButtonSize.Small}
          variant={EIconButtonVariant.Primary}
          onClick={onModalClose}
        />
      </div>

      <Carousel
        content={[
          <ClaimsGuideCarouselStartSlide key="start" onStart={handleStart} />,
          <ClaimsGuideCarouselSlide
            key="#1"
            imageSrc={SlideN1Image.src}
            linkText={intl.formatMessage({ defaultMessage: 'Watch a video', id: 'R04xLa' })}
            linkHref="https://www.youtube.com/watch?v=hx0EZMpWboM"
            number="#1"
            title={intl.formatMessage({
              defaultMessage: 'Scraping the RV against trees',
              id: 'MK8uaZ',
            })}
            description={intl.formatMessage({
              defaultMessage:
                'Tight squeeze? Take it slow, use your mirrors, and be aware of what’s around you. Take ‘wider’ turns, but be careful when you do.',
              id: 'B5SdZA',
            })}
          />,
          <ClaimsGuideCarouselSlide
            key="#2"
            imageSrc={SlideN2Image.src}
            linkText={intl.formatMessage({ defaultMessage: 'Read more', id: 'S++WdB' })}
            linkHref="/blog/tips-fueling-up-your-rv"
            number="#2"
            title={intl.formatMessage({
              defaultMessage: 'Damage at the gas station',
              id: 'FDSrXJ',
            })}
            description={intl.formatMessage({
              defaultMessage:
                'Look up! An RV is twice as tall as a car. Check the spacing and height clearance at gas stations and other low-clearance areas.',
              id: 'Y4sMya',
            })}
          />,
          <ClaimsGuideCarouselSlide
            key="#3"
            imageSrc={SlideN3Image.src}
            linkText={intl.formatMessage({ defaultMessage: 'Watch a video', id: 'R04xLa' })}
            linkHref="https://www.youtube.com/watch?v=hx0EZMpWboM"
            number="#3"
            title={intl.formatMessage({
              defaultMessage: 'Hitting objects while backing up',
              id: 'DEGoQI',
            })}
            description={intl.formatMessage({
              defaultMessage:
                'Check for people, trees, tables, fire pits, etc. Be aware of your RV’s length and don’t be afraid to ask for a spotter to guide you.',
              id: 'nXQI7Z',
            })}
          />,
          <ClaimsGuideCarouselSlide
            key="#4"
            imageSrc={SlideN4Image.src}
            linkText={intl.formatMessage({ defaultMessage: 'Read more', id: 'S++WdB' })}
            linkHref="/blog/how-to-guide-for-rv-renter-newbies"
            number="#4"
            title={intl.formatMessage({
              defaultMessage: 'Colliding with other vehicles',
              id: 'Vdq7Xa',
            })}
            description={intl.formatMessage({
              defaultMessage:
                'Drive slowly and brake early when you’re on the road. Be safe and check your blind spots when switching lanes.',
              id: 'jCGSUJ',
            })}
          />,
          <ClaimsGuideCarouselSlide
            key="#5"
            imageSrc={SlideN5Image.src}
            linkText={intl.formatMessage({ defaultMessage: 'Watch a video', id: 'R04xLa' })}
            linkHref="https://www.youtube.com/watch?v=Djh_s7emMmo"
            number="#5"
            title={intl.formatMessage({
              defaultMessage: 'Weather damage to the awning',
              id: 'R6PGqY',
            })}
            description={intl.formatMessage({
              defaultMessage:
                'Always close the awning before moderate weather or wind comes in, when it’s not in use, and at night.',
              id: 'CQ0Ith',
            })}
          />,
        ]}
        onSwiper={setSwiperInstance}
        onActiveIndexChange={swiper => {
          setSlideNumber(swiper.activeIndex + 1);

          if (swiper.isEnd && !trackedViewedEventRef.current) {
            trackClaimsCarouselViewedEvent({ slideNumber: swiper.activeIndex + 1 });
            trackedViewedEventRef.current = true;
          }
        }}
        autoplay={{
          delay: 6000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }}
        onToEdge={swiper => {
          if (swiper.isEnd) swiper.autoplay.stop();
        }}
        noNavigationArrows
        removePadding
      />

      <div className="hidden md:py-4 md:flex md:justify-center">
        <Pagination
          pageNumber={slideNumber}
          onChange={handleSlideChange}
          variant={EPaginationVariant.PageNumbers}
          pretext={intl.formatMessage({ defaultMessage: 'of', id: 'C9WGEu' })}
          ariaLabel={intl.formatMessage({ defaultMessage: 'Carousel slides switch', id: 'lKudMA' })}
          previousPageAriaLabel={intl.formatMessage({
            defaultMessage: 'Previous slide',
            id: 'MH3koz',
          })}
          nextPageAriaLabel={intl.formatMessage({ defaultMessage: 'Next slide', id: 'Y9bYKH' })}
          totalPages={6}
        />
      </div>
    </div>
  );
};
